import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ImagePlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const field = "w-full rounded-sm border border-[#2A2E37] bg-white/5 px-4 py-2.5 text-sm outline-none focus:border-white/40";

function ImageField({ kind, label, onDone }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    try { onDone((await api.post("/me/image", fd)).data); toast.success(`${label} updated`); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-sm border border-dashed border-[#2A2E37] px-4 py-3 text-sm text-gray-300 transition-colors hover:border-white/25">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} {label}
      <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`upload-${kind}-input`} />
    </label>
  );
}

export default function EditProfileDialog({ open, onOpenChange, onSaved }) {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({});
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({ name: user.name, bio: user.bio, is_creator: user.is_creator, price_tier: user.price_tier || "creator_sub_999" });
      api.get("/price-tiers").then((r) => setTiers(r.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updated = (u) => { setUser(u); onSaved?.(); };

  const save = async () => {
    setSaving(true);
    try {
      updated((await api.put("/me", form)).data);
      toast.success("Profile saved");
      onOpenChange(false);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md border-[#2A2E37] bg-[#121418] text-white" data-testid="edit-profile-dialog">
        <DialogHeader><DialogTitle className="font-display text-lg font-semibold">Edit profile</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <ImageField kind="avatar" label="Avatar" onDone={updated} />
            <ImageField kind="banner" label="Banner" onDone={updated} />
          </div>
          <input className={field} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Display name" data-testid="edit-name-input" />
          <textarea className={`${field} min-h-[90px]`} value={form.bio || ""} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Bio" data-testid="edit-bio-input" />
          <div className="flex items-center justify-between rounded-sm border border-[#2A2E37] bg-white/5 px-4 py-3">
            <div>
              <p className="text-sm font-semibold">Creator mode</p>
              <p className="text-xs text-gray-400">Sell monthly subscriptions to your content</p>
            </div>
            <Switch checked={!!form.is_creator} onCheckedChange={(v) => setForm({ ...form, is_creator: v })} data-testid="creator-mode-switch" />
          </div>
          {form.is_creator && (
            <Select value={form.price_tier} onValueChange={(v) => setForm({ ...form, price_tier: v })}>
              <SelectTrigger className="rounded-sm border-[#2A2E37] bg-white/5" data-testid="price-tier-select"><SelectValue placeholder="Monthly price" /></SelectTrigger>
              <SelectContent className="border-[#2A2E37] bg-[#121418] text-white">
                {tiers.map((t) => <SelectItem key={t.lookup_key} value={t.lookup_key} data-testid={`price-tier-${t.lookup_key}`}>${t.amount} / month</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <button onClick={save} disabled={saving} data-testid="save-profile-btn" className="btn-neon flex w-full items-center justify-center gap-2 rounded-sm py-3 font-semibold text-white">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
